import { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export default function VerifyEmail() {
  const location = useLocation();
  const { resendVerificationEmail } = useAuth();
  const email = (location.state as { email?: string })?.email || '';
  
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleResend = async () => {
    if (!email) {
      setErrorMessage('이메일 주소를 찾을 수 없습니다. 다시 회원가입을 진행해주세요.');
      setResendStatus('error');
      return;
    }

    setIsResending(true);
    setResendStatus('idle');

    try {
      await resendVerificationEmail(email);
      setResendStatus('success');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : '메일 전송에 실패했습니다');
      setResendStatus('error');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full text-center">
        <div className="card">
          <div className="w-16 h-16 bg-space-blue-light rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-space-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 dark:text-white dark:text-white mb-2">
            이메일을 확인해주세요
          </h1>

          <p className="text-gray-700 dark:text-gray-300 mb-6">
            <span className="font-medium text-gray-900 dark:text-white dark:text-white">{email}</span>
            {email ? '로' : ''} 인증 메일을 발송했습니다.
            <br />
            메일함을 확인하고 인증 링크를 클릭해주세요.
          </p>

          {resendStatus === 'success' && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm mb-4">
              ✅ 인증 메일을 다시 보냈습니다. 메일함을 확인해주세요.
            </div>
          )}

          {resendStatus === 'error' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm mb-4">
              {errorMessage}
            </div>
          )}

          <div className="bg-gray-100 dark:bg-zinc-800 rounded-lg p-4 mb-6 text-sm text-gray-700 dark:text-gray-300">
            <p>📧 메일이 오지 않았나요?</p>
            <ul className="mt-2 text-left list-disc list-inside space-y-1">
              <li>스팸 메일함을 확인해주세요</li>
              <li>이메일 주소가 올바른지 확인해주세요</li>
              <li>몇 분 후에 다시 시도해주세요</li>
            </ul>
          </div>

          <div className="space-y-3">
            <button 
              onClick={handleResend}
              disabled={isResending}
              className="w-full btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending ? '전송 중...' : '인증 메일 다시 보내기'}
            </button>
            <Link to="/login" className="block text-space-blue hover:underline text-sm">
              로그인 페이지로 돌아가기
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
